<template lang="pug">
Modal(
  :cancel-action='close'
  :disable-process-button='!testedOK'
  :loading='loading'
  :process-button-label='$t("integrations.saveIntegration")'
  :remove-close-button='false'
  :show-cancel-button='false'
  :title='$t("integrations.integrationWithName", { name: integration.name })'
  @process='onSave'
  process-button-class='btn-save-integration'
  ref='modal'
  size='md'
)
  form.form-container(@submit.prevent='onSave')
    template(v-if='currentSchema.length')
      .mb-3(:key='field.key' v-for='field in initialFields')
        label.form-label(:for='field.key')
          | {{ $t(field.label) }}
          span.text-required(v-if='field.required') *
        .icon-input
          Icon.abs-icon(:name='field.icon')
          input.form-control(
            :id='field.key'
            :placeholder='$t(field.placeholder)'
            :required='field.required'
            :type='getType(field)'
            v-model='form[field.key]'
          )
          span.abs-toggle(
            @click='toggleShowPassword(field.key)'
            v-if='field.type === "password"'
          )
            Icon(:name='showPassword[field.key] ? "eye-slash" : "eye"')

      .row.g-3
        .col-12.col-md-6.mb-3(:key='field.key' v-for='field in lastTwoFields')
          label.form-label(:for='field.key')
            | {{ $t(field.label) }}
            span.text-required(v-if='field.required') *
          .icon-input
            Icon.abs-icon(:name='field.icon')
            input.form-control(
              :id='field.key'
              :placeholder='$t(field.placeholder)'
              :required='field.required'
              :type='getType(field)'
              v-model='form[field.key]'
            )
            span.abs-toggle(
              @click='toggleShowPassword(field.key)'
              v-if='field.type === "password"'
            )
              Icon(:name='showPassword[field.key] ? "eye-slash" : "eye"')

      .text-center.mb-4
        button.btn.btn-link.test-link(
          :disabled='!canTest || testing'
          @click='onTest'
          type='button'
          v-if='!testing && testResult !== "success"'
        ) {{ $t('integrations.testIntegration') }}
        span.badge.status-badge(v-if='testing')
          | {{ $t('integrations.testingIntegration') }}
          Icon.ms-2(name='clock')

      .text-center.mb-3
        p.text-success(v-if='testResult === "success"')
          | {{ $t('integrations.testSuccess') }}
          |
          Icon(name='circle-check')
        p.text-danger(v-else-if='testResult === "error"')
          | {{ $t('integrations.testError') }}
          Icon.ms-2(name='circle-exclamation')

    template(v-else)
      .alert.alert-warning(text-center)
        | {{ $t('integrations.noConfigurationFields') }}
</template>

<script>
import Modal from '@/ui/molecules/Modal.vue';
import Icon from '@/ui/atoms/Icon.vue';
import schemas from '@/config/integrationSchemas.js';
import IntegrationsService from '@/services/IntegrationsService';

export default {
  name: 'IntegrationConfigureModal',
  components: { Modal, Icon },
  props: {
    integration: { type: Object, required: true },
  },
  data() {
    return {
      loading: false,
      testing: false,
      testedOK: false,
      testResult: null,
      form: {},
      showPassword: {},
    };
  },
  computed: {
    currentSchema() {
      return schemas[this.integration.type] || [];
    },
    initialFields() {
      return this.currentSchema.slice(0, -2);
    },
    lastTwoFields() {
      return this.currentSchema.slice(-2);
    },
    canTest() {
      return this.currentSchema.every((f) =>
        f.required ? !!this.form[f.key] : true
      );
    },
  },
  watch: {
    form: {
      handler() {
        this.testedOK = false;
        this.testResult = null;
      },
      deep: true,
    },
  },
  methods: {
    getType(field) {
      return field.type !== 'password'
        ? field.type
        : this.showPassword[field.key]
        ? 'text'
        : 'password';
    },
    open() {
      const cfg = this.integration.config || {};
      this.currentSchema.forEach((f) => {
        this.$set(this.form, f.key, cfg[f.key] || '');
        if (f.type === 'password') {
          this.$set(this.showPassword, f.key, false);
        }
      });
      this.testing = false;
      this.testResult = null;
      this.testedOK = false;
      this.$refs.modal.open();
    },
    close() {
      this.$refs.modal.close();
    },
    toggleShowPassword(key) {
      this.showPassword[key] = !this.showPassword[key];
    },
    async onTest() {
      this.testing = true;
      this.testResult = null;
      try {
        const organizationId = this.$store.state.organizations.selected.id;
        await IntegrationsService.login(
          organizationId,
          this.integration.type,
          this.form
        );
        this.testResult = 'success';
        this.testedOK = true;
      } catch {
        this.testResult = 'error';
        this.testedOK = false;
      } finally {
        this.testing = false;
      }
    },
    async onSave() {
      this.loading = true;
      const organizationId = this.$store.state.organizations.selected.id;
      const isUpdate =
        this.integration.id &&
        this.integration.config &&
        Object.keys(this.integration.config).length > 0;
      try {
        if (isUpdate) {
          await IntegrationsService.update(
            organizationId,
            this.integration.id,
            this.integration.type,
            this.form
          );
        } else {
          await IntegrationsService.create(
            organizationId,
            this.integration.type,
            this.form
          );
        }
        this.$emit('saved', { name: this.integration.name });
        this.close();
      } catch (err) {
        this.$toast('Erro ao salvar: ' + (err.message || err), {
          type: 'error',
        });
        alert.fireAlert(
          'Erro ao salvar integração: ' +
            (err.response?.data?.message || err.message || err),
          { classes: 'alert-danger', icon: 'triangle-exclamation' }
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.form-container {
  padding: 0 24px;
}
::v-deep .form-label {
  margin-bottom: 4px !important;
}
.icon-input {
  position: relative;
  margin-top: 4px;
}
.icon-input .form-control {
  width: 100%;
  height: 48px;
  padding: 0 2.5rem;
  border: 1px solid #ced4da;
  border-radius: 15px;
  box-sizing: border-box;
}
.abs-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #19665b;
  pointer-events: none;
}
.abs-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #495057;
}
::v-deep .modal-dialog.modal-md {
  max-width: 592px;
  max-height: 552px;
}
::v-deep .modal-header {
  border-bottom: none !important;
}
.test-link {
  color: #0080a3 !important;
  text-decoration: underline;
  font-weight: 500;
}
.status-badge {
  background: #e6e6e6;
  color: #0080a3;
  padding: 0.5em 1em;
  font-weight: 500;
}
.text-success {
  display: inline-block;
  padding: 0.25em 0.5em;
  background-color: #f1faeb;
  color: black !important;
}
.text-danger {
  display: inline-block;
  padding: 0.25em 0.5em;
  color: black !important;
  background: #fff6e5;
}
.text-required {
  color: red;
}
.alert-warning {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  border-radius: 4px;
  text-align: center;
}
::v-deep button.btn-save-integration {
  background: #19665b !important;
  border-color: #19665b !important;
  color: #fff !important;
  padding: 8px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}
::v-deep button.btn-save-integration:disabled {
  background: #808080 !important;
  border-color: #808080 !important;
  color: #fff !important;
}
::v-deep .modal-body {
  padding: 0px;
}
::v-deep .modal-footer {
  border-top: none !important;
  padding: 0 24px 24px;
}
::v-deep .modal-title {
  font-weight: 600;
}
::v-deep .close {
  font-weight: 300;
  color: #2f3242;
}
</style>
